var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { RenderTransparencyMessage, RenderColorMessage, RemoveModelMessage, MeshFacet, LocalTransformFacet, VisibilityFacet, DeleteModelMessage, NetworkFacet, } from "flavr-common";
import { busService } from "@modules/bus";
import { modelPool, useExternalMessages, determineModelNameForPrinting, computeLocalMatrixFromWorldMatrix } from "@modules/model";
// Low opacity + green tint to distinguish previews from the held/source mesh.
const PREVIEW_OPACITY = 0.1;
const PREVIEW_COLOR = 0x33ff66;
export class DropOffPreviewManager {
    constructor() {
        this.connection = busService.connection("DropOffPreviewService");
        this.modelUIDToPreview = new Map();
    }
    /// Getter and setter ///
    modelHasPreview(previewID) {
        return this.getActivePreview(previewID) !== undefined;
    }
    getPreviewModel(previewID) {
        var _a;
        return (_a = this.getActivePreview(previewID)) === null || _a === void 0 ? void 0 : _a.previewModel;
    }
    getActivePreview(previewID) {
        const info = this.modelUIDToPreview.get(previewID);
        if (info === undefined)
            return undefined;
        if (modelPool.getModel(info.previewUID) !== info.previewModel) {
            this.modelUIDToPreview.delete(previewID);
            console.warn(`[pickup] Discarding removed drop-off preview ${previewID}`);
            return undefined;
        }
        return info;
    }
    /**
     * Returns the preview model's current world transform as last set by `updatePreview()`.
     */
    getPreviewWorldTransform(previewID) {
        var _a;
        const previewInfo = this.getActivePreview(previewID);
        if (previewInfo === undefined)
            return undefined;
        return (_a = modelPool.getModel(previewInfo.previewUID)) === null || _a === void 0 ? void 0 : _a.getWorldTransform();
    }
    /// Preview creation and destruction ///
    createPreview(previewID, modelUID, parentUID) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            if (this.modelHasPreview(previewID)) {
                console.warn(`[pickup] A preview for ${determineModelNameForPrinting(modelUID)} with ID ${previewID} already exists`);
                return undefined;
            }
            const model = modelPool.getModel(modelUID);
            if (model === undefined) {
                console.warn("[model] Model doesn't exist");
                return undefined;
            }
            // Keep previews in the room tree first; XR controller/grip parents may be transient or hidden.
            const parent = (_b = (_a = model.getRoom()) !== null && _a !== void 0 ? _a : modelPool.getCurrentRoom()) !== null && _b !== void 0 ? _b : modelPool.getModel(parentUID);
            if (parent === undefined) {
                console.warn("[model] Parent doesn't exist");
                return undefined;
            }
            const cloneName = `dropOffPreview_${previewID}`;
            const clonedModel = this.createClone(cloneName, parent, model);
            if (clonedModel === undefined) {
                console.warn(`Cloning model ${model.getName()} failed`);
                return undefined;
            }
            const previewUID = clonedModel.getUID();
            const info = { modelUID, previewUID, previewModel: clonedModel, visible: false };
            this.modelUIDToPreview.set(previewID, info);
            const messages = [];
            this.applyPreviewVisibility(clonedModel, model, false, messages);
            this.connection.sendMultiple(messages);
        });
    }
    updatePreview(previewID, worldTransform) {
        return __awaiter(this, void 0, void 0, function* () {
            const previewInfo = this.getActivePreview(previewID);
            if (previewInfo === undefined) {
                return;
            }
            const messages = [];
            this.updateModelTransform(previewInfo.previewUID, worldTransform, messages);
            // We show the model once we updated its position
            if (!previewInfo.visible) {
                // First valid transform turns the hidden clone into the visible preview state.
                this.applyVisiblePreviewState(previewInfo, messages);
            }
            this.connection.sendMultiple(messages);
        });
    }
    removePreview(previewID) {
        const previewInfo = this.getActivePreview(previewID);
        if (previewInfo === undefined)
            return;
        // Remove from local map first so in-flight sync/update calls see it as gone.
        this.modelUIDToPreview.delete(previewID);
        this.removeClone(previewInfo.previewUID);
    }
    showPreview(previewID) {
        return __awaiter(this, void 0, void 0, function* () {
            const previewInfo = this.getActivePreview(previewID);
            if (previewInfo === undefined)
                return;
            const messages = [];
            // showPreview and first-time updatePreview share the same "visible preview" state setup.
            this.applyVisiblePreviewState(previewInfo, messages);
            this.connection.sendMultiple(messages);
        });
    }
    hidePreview(previewID) {
        return __awaiter(this, void 0, void 0, function* () {
            const previewInfo = this.getActivePreview(previewID);
            if (previewInfo === undefined)
                return;
            const messages = [];
            const previewModel = modelPool.getModel(previewInfo.previewUID);
            // Keep hidden-state child visibility in sync so re-show uses current source VisibilityFacet values.
            if (previewModel !== undefined)
                this.applyPreviewVisibility(previewModel, modelPool.getModel(previewInfo.modelUID), false, messages);
            this.changeModelAppearance(previewInfo.previewModel, 0.0, undefined, messages);
            previewInfo.visible = false;
            this.connection.sendMultiple(messages);
        });
    }
    /// Cloning ///
    createClone(cloneName, parent, srcModel) {
        try {
            // We only want to clone the appearance of the grabbed model
            const facetWhitelist = [
                MeshFacet,
                LocalTransformFacet,
                VisibilityFacet,
                NetworkFacet,
            ];
            const previewMessages = [];
            const props = useExternalMessages(previewMessages);
            // Clone only render/transform-relevant facets to keep previews lightweight and behavior-free.
            const clone = modelPool.cloneModel(srcModel, Object.assign({ name: cloneName, parent, facetWhitelist, allowOverwritingModel: true }, props));
            // Disable shadows on preview mesh - check all models in the hierarchy
            clone.traverse(model => {
                const meshFacet = model.getFacet(MeshFacet);
                if (meshFacet !== undefined) {
                    meshFacet.castShadow = false;
                    meshFacet.receiveShadow = false;
                }
            });
            if (previewMessages.length > 0)
                this.connection.sendMultiple(previewMessages);
            return clone;
        }
        catch (error) {
            console.error("[pickup] Error while creating model preview", error);
            return undefined;
        }
    }
    removeClone(clonedModelUid) {
        this.connection.sendMultiple([
            new RemoveModelMessage(clonedModelUid),
            new DeleteModelMessage(clonedModelUid), // deletes it from the server
        ]);
    }
    /// Modify model ///
    changeModelAppearance(previewModel, opacity, color, messages) {
        // Apply appearance recursively so nested children get the same preview tint/alpha.
        previewModel.traverse(model => {
            messages.push(new RenderTransparencyMessage(model.getUID(), 1.0 - opacity));
            if (color !== undefined)
                messages.push(new RenderColorMessage(model.getUID(), color));
        });
    }
    applyVisiblePreviewState(previewInfo, messages) {
        const previewModel = previewInfo.previewModel;
        const sourceModel = modelPool.getModel(previewInfo.modelUID);
        // Visibility is synchronized from the currently held source model before showing the preview.
        if (previewModel !== undefined && sourceModel !== undefined)
            this.applyPreviewVisibility(previewModel, sourceModel, true, messages);
        // Apply the canonical preview look every time it becomes visible.
        this.changeModelAppearance(previewModel, PREVIEW_OPACITY, PREVIEW_COLOR, messages);
        previewInfo.visible = true;
    }
    updateModelTransform(uid, transform, messages) {
        const model = modelPool.getModel(uid);
        if (model === undefined) {
            console.warn(`[pickup] Model ${determineModelNameForPrinting(uid)} transform cannot be updated, because it doesn't exist`);
            return;
        }
        const localMatrix = computeLocalMatrixFromWorldMatrix(model, transform);
        model.setTransform(localMatrix, useExternalMessages(messages));
    }
    applyPreviewVisibility(previewModel, sourceModel, previewVisible, messages, parentVisible = true) {
        var _a, _b, _c;
        const ownVisible = (_b = (_a = sourceModel === null || sourceModel === void 0 ? void 0 : sourceModel.getFacet(VisibilityFacet)) === null || _a === void 0 ? void 0 : _a.visible) !== null && _b !== void 0 ? _b : true;
        const visible = previewVisible && parentVisible && ownVisible;
        previewModel.setVisible(visible, useExternalMessages(messages));
        const previewChildren = previewModel.getChildren();
        const sourceChildren = (_c = sourceModel === null || sourceModel === void 0 ? void 0 : sourceModel.getChildren()) !== null && _c !== void 0 ? _c : [];
        const childCount = previewChildren.length;
        // Keep preview tree visibility aligned with the source tree recursively.
        for (let i = 0; i < childCount; i++) {
            const previewChild = previewChildren[i];
            const sourceChild = sourceChildren[i];
            this.applyPreviewVisibility(previewChild, sourceChild, previewVisible, messages, visible);
        }
    }
}
